import { Account } from '@betterfunnel/types';
import AlertModal from '@betterfunnel/ui-core/AlertModal';
import MenuButton from '@betterfunnel/ui-core/MenuButton';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { QueryDocumentSnapshot, updateDoc } from 'firebase/firestore';
import { Fragment, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import icon from '../assets/images/logo.png';
import { AuthContext } from '../contexts/AuthContext';
import { UserContext } from '../contexts/UserContext';
import CreateWorkspaceDialog from '../modals/CreateWorkspaceModal';
import { track } from '../utils/analytics';

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(' ');
}

export default function Workspaces() {
  const { logout } = useContext(AuthContext);
  const { accounts, refetchAccounts } = useContext(UserContext);

  const [createWorkspaceDialogOpen, setCreateWorkspaceDialogOpen] =
    useState(false);
  const [showDeleteWorkspaceModal, setShowDeleteWorkspaceModal] =
    useState<QueryDocumentSnapshot<Account>>();

  const userNavigation = useMemo(
    () => [
      // { name: 'Your Profile', href: '#' },
      // { name: 'Settings', href: '#' },
      {
        name: 'Sign out',
        onClick: () => {
          logout();
        },
      },
    ],
    [logout],
  );

  return (
    <>
      <CreateWorkspaceDialog
        open={createWorkspaceDialogOpen}
        onClose={() => setCreateWorkspaceDialogOpen(false)}
      />
      <AlertModal
        type="warn"
        title={`Are you sure you'd like to delete account ${
          showDeleteWorkspaceModal?.data().name
        }?`}
        description="This is not reversible"
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => {
              setShowDeleteWorkspaceModal(undefined);
            },
          },

          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            onClick: async () => {
              const account = showDeleteWorkspaceModal;
              if (!account) return;

              track('delete_workspace_from_workspace_menu_approved_click', {
                accountId: account.id,
              });
              setShowDeleteWorkspaceModal(undefined);

              updateDoc(account.ref, { status: 'deleted' }).then(() =>
                refetchAccounts(),
              );
            },
          },
        ]}
        open={!!showDeleteWorkspaceModal}
        onClose={() => {
          setShowDeleteWorkspaceModal(undefined);
        }}
      />
      <div className="flex h-full flex-col">
        <Header userNavigation={userNavigation} />
        <div className="mx-auto mt-16 flex w-full max-w-7xl flex-1 flex-col px-4 pb-6 lg:px-8">
          <Heading
            onNewWorkspaceClick={() => {
              track('create_workspace_button_click');
              setCreateWorkspaceDialogOpen(true);
            }}
          />
          {accounts?.length ? (
            <div className="pt-8">
              <WorkspacesList
                accounts={accounts}
                onDeleteAccountClick={(account) =>
                  setShowDeleteWorkspaceModal(account)
                }
              />
            </div>
          ) : (
            <></>
          )}
          {accounts?.length === 0 ? (
            <div className="flex h-auto items-center justify-center pb-60 sm:h-full">
              <NoAccounts
                onNewWorkspaceClick={() => {
                  track('create_workspace_button_click');
                  setCreateWorkspaceDialogOpen(true);
                }}
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </>
  );
}

function Header({ userNavigation }: { userNavigation: any[] }) {
  const { firebaseUser } = useContext(AuthContext);

  return (
    <Disclosure as="header" className="bg-white shadow-sm">
      {({ open }) => (
        <>
          <div className="mx-auto max-w-7xl px-2 sm:px-4 lg:divide-y lg:divide-gray-200 lg:px-8">
            <div className="relative flex h-16 justify-between">
              <div className="relative z-10 flex px-2 lg:px-0">
                <div className="flex shrink-0 items-center">
                  <img className="h-8 w-auto" src={icon} alt="BetterFunnel" />
                </div>
              </div>
              {/* <div className="relative z-0 flex flex-1 items-center justify-center px-2 sm:absolute sm:inset-0">
                <div className="w-full sm:max-w-xs">
                  <label htmlFor="search" className="sr-only">
                    Search
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <MagnifyingGlassIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      id="search"
                      name="search"
                      className="block w-full rounded-md border-0 bg-white py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Search"
                      type="search"
                    />
                  </div>
                </div>
              </div> */}
              <div className="relative z-10 flex items-center lg:hidden">
                {/* Mobile menu button */}
                <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-hidden focus:ring-inset">
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
              <div className="hidden lg:relative lg:z-10 lg:ml-4 lg:flex lg:items-center">
                {/* <button
                  type="button"
                  className="relative shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </button> */}

                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-4 shrink-0">
                  <div>
                    <Menu.Button className="relative flex rounded-full bg-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden">
                      <span className="absolute -inset-1.5" />
                      <span className="sr-only">Open user menu</span>
                      {firebaseUser?.photoURL ? (
                        <img
                          className="h-8 w-8 rounded-full"
                          src={firebaseUser?.photoURL}
                          alt=""
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-gray-100" />
                      )}
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                      {userNavigation.map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <button
                              onClick={() => item.onClick?.()}
                              className={classNames(
                                active ? 'bg-gray-100' : '',
                                'block w-full px-4 py-2 text-start text-sm text-gray-700',
                              )}
                            >
                              {item.name}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <Disclosure.Panel as="nav" className="lg:hidden" aria-label="Global">
            <div className="border-t border-gray-200 pt-4 pb-3">
              <div className="flex items-center px-4">
                <div className="shrink-0">
                  {firebaseUser?.photoURL ? (
                    <img
                      className="h-10 w-10 rounded-full"
                      src={firebaseUser?.photoURL}
                      alt=""
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-100" />
                  )}
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {firebaseUser?.displayName ?? ''}
                  </div>
                  <div className="text-sm font-medium text-gray-500">
                    {firebaseUser?.email ?? ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="relative ml-auto shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-3 space-y-1 px-2">
                {userNavigation.map((item) => (
                  <Disclosure.Button
                    key={item.name}
                    as="a"
                    href={item.href}
                    className="block rounded-md px-3 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                  >
                    {item.name}
                  </Disclosure.Button>
                ))}
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}

function Heading({ onNewWorkspaceClick }: { onNewWorkspaceClick: () => void }) {
  const { firebaseUser } = useContext(AuthContext);

  const firstName = useMemo(
    () => firebaseUser?.displayName?.split(' ')[0],
    [firebaseUser],
  );

  return (
    <div className="flex items-center justify-between lg:border-b lg:border-b-gray-200 lg:pb-4">
      <div className="min-w-0 flex-1">
        <h2 className="text-2xl leading-7 font-bold text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
          Welcome{firstName ? `, ${firstName}.` : ''}
        </h2>
        {/* <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
          <div className="mt-2 flex items-center text-sm text-gray-500">
            You are amazing...
          </div>
        </div> */}
      </div>
      <div
      // className="ml-2 flex sm:mt-5 lg:ml-4"
      >
        <span className="sm:ml-3">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid"
            onClick={onNewWorkspaceClick}
          >
            <PlusIcon className="mr-1.5 -ml-0.5 h-5 w-5" aria-hidden="true" />
            New Workspace
          </button>
        </span>
      </div>
    </div>
  );
}

function NoAccounts({
  onNewWorkspaceClick,
}: {
  onNewWorkspaceClick: () => void;
}) {
  return (
    <div className="text-center">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
        />
      </svg>
      <h3 className="mt-2 text-sm font-semibold text-gray-900">
        No workspaces
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        Get started by creating a new workspace.
      </p>
      <div className="mt-6">
        <button
          type="button"
          className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid"
          onClick={onNewWorkspaceClick}
        >
          <PlusIcon className="mr-1.5 -ml-0.5 h-5 w-5" aria-hidden="true" />
          New Workspace
        </button>
      </div>
    </div>
  );
}

function WorkspacesList({
  accounts,
  onDeleteAccountClick,
}: {
  accounts: QueryDocumentSnapshot<Account>[];
  onDeleteAccountClick: (account: QueryDocumentSnapshot<Account>) => void;
}) {
  const navigate = useNavigate();
  const { isSupportAdmin } = useContext(AuthContext);

  return (
    <ul
      role="list"
      className="grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-3 xl:gap-x-8"
    >
      {accounts.map((account) => {
        const accountData = account.data();
        return (
          <li
            key={account.id}
            className="cursor-pointer overflow-hidden rounded-xl border border-gray-200 hover:border-gray-400"
            onClick={() => {
              navigate('/workspace/' + accountData.slug);
            }}
          >
            <div className="flex items-center gap-x-4 border-b border-gray-900/5 bg-gray-50 p-6">
              {accountData.logoUrl ? (
                <img
                  src={accountData.logoUrl}
                  alt={accountData.name}
                  className="h-12 w-12 flex-none rounded-lg bg-white object-contain p-1 ring-1 ring-gray-900/10"
                />
              ) : (
                <div className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-white object-cover ring-1 ring-gray-900/10">
                  <span className="-mt-1 text-lg font-bold text-gray-400">
                    {accountData.name.slice(0, 1)}
                  </span>
                </div>
              )}
              <div className="text-sm leading-6 font-medium text-gray-900">
                {accountData.name}
              </div>
              <div className="ml-auto" onClick={(e) => e.stopPropagation()}>
                <MenuButton
                  items={[
                    {
                      title: 'View',
                      onClick: () => navigate('/workspace/' + accountData.slug),
                    },
                    ...(isSupportAdmin
                      ? [
                          {
                            title: 'Delete',
                            destructive: true,
                            onClick: () => onDeleteAccountClick(account),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            </div>
            <dl className="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm leading-6">
              <div className="flex justify-between gap-x-4 py-3">
                <dt className="text-gray-500">Created at</dt>
                <dd className="text-gray-700">
                  {/* <time dateTime={accountData.lastInvoice.dateTime}>
                    {client.lastInvoice.date}
                  </time> */}
                  <time>
                    {accountData.createdAt.toDate().toLocaleDateString()}
                  </time>
                </dd>
              </div>
              {/* <div className="flex justify-between gap-x-4 py-3">
                <dt className="text-gray-500">Amount</dt>
                <dd className="flex items-start gap-x-2">
                  <div className="font-medium text-gray-900">
                    {client.lastInvoice.amount}
                  </div>
                  <div
                    className={classNames(
                      (statuses as any)[client.lastInvoice.status],
                      'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset',
                    )}
                  >
                    {client.lastInvoice.status}
                  </div>
                </dd>
              </div> */}
            </dl>
          </li>
        );
      })}
    </ul>
  );
}
